import React, { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useNavigate, useSearchParams } from "react-router-dom";

import global from "../../styles/globalStyles";
import {
  getPendingPostAuthRedirectPath,
  getPostAuthRedirectPath,
} from "../../utils/authRedirect";
import { supabase } from "../../utils/supabase";

import {
  Input,
  Header,
  TextLink,
  PrimaryButton,
  OrSeperator,
  FooterLinks,
  GoogleLoginBtn,
} from "../common/native/index";

const Signin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const canSubmitWithEmail = Boolean(email.trim() && password);
  const postAuthRedirectPath = getPostAuthRedirectPath(
    searchParams.get("returnTo") || getPendingPostAuthRedirectPath(),
  );
  const oauthCallbackPath = `/signin?returnTo=${encodeURIComponent(postAuthRedirectPath)}`;

  React.useEffect(() => {
    let isMounted = true;

    const redirectIfAuthenticated = async () => {
      const { data } = await supabase.auth.getSession();
      if (!isMounted || !data.session) return;
      navigate(postAuthRedirectPath, { replace: true });
    };

    redirectIfAuthenticated();

    const { data: authListener } = supabase.auth.onAuthStateChange((event) => {
      if (!isMounted || event !== "SIGNED_IN") return;
      navigate(postAuthRedirectPath, { replace: true });
    });

    return () => {
      isMounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [navigate, postAuthRedirectPath]);

  const login = (email: string, password: string) =>
    supabase.auth.signInWithPassword({ email, password });

  const handleOnPressLogInWithEmail = async () => {
    setLoading(true);
    try {
      const { data, error } = await login(email, password);
      if (error) setErrorMessage(error.message);
      if (!error) {
        navigate(postAuthRedirectPath);
      }
    } catch (error) {
      setErrorMessage("Error in Logging In");
    }
    setLoading(false);
  };

  const handleSubmitFromKeyboard = () => {
    if (!canSubmitWithEmail || loading) return;
    handleOnPressLogInWithEmail();
  };

  return (
    <View style={styles.container}>
      <Header />
      <View
        style={{
          width: "100%",
          maxWidth: 434,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Text style={[global.title, { marginBottom: 12 }]}>Welcome Back</Text>
        <View style={styles.signupPrompt}>
          <Text style={[global.text, global.fontWeight600, global.fontSize18]}>
            New to ClinicOS?
          </Text>
          <TextLink
            to={
              postAuthRedirectPath === "/home"
                ? "/signup"
                : `/signup?returnTo=${encodeURIComponent(postAuthRedirectPath)}`
            }
            text={"Sign up"}
            style={[global.blue, global.fontSize18]}
          />
        </View>
        <View style={styles.oauthButtons}>
          <GoogleLoginBtn
            name={"Login with Google"}
            redirectTo={oauthCallbackPath}
          />
          <GoogleLoginBtn
            name={"Login with Apple"}
            provider="apple"
            redirectTo={oauthCallbackPath}
          />
        </View>
        <OrSeperator />
        {errorMessage !== "" && (
          <Text style={global.errorText}>{errorMessage}</Text>
        )}
        <Input
          type="email-address"
          name="Email"
          placeholder="name@work-email.com"
          value={email}
          onChangeText={setEmail}
          onSubmitEditing={handleSubmitFromKeyboard}
          focusBackgroundColor="#F1FAFF"
          focusBorderColor="#C4EEFD"
          required
        />
        <Input
          name="Password"
          placeholder="Enter your password"
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={handleSubmitFromKeyboard}
          focusBackgroundColor="#F1FAFF"
          focusBorderColor="#C4EEFD"
          secureTextEntry
          required
        />
        <View style={styles.forgotPasswordContainer}>
          <TextLink to="/forgot-password" text="Forgot password?" />
        </View>
        <PrimaryButton
          text="Log In with Email"
          fullWidth
          onPress={handleOnPressLogInWithEmail}
          disabled={!canSubmitWithEmail}
          loading={loading}
        />
      </View>
      <FooterLinks />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    alignSelf: "center",
    justifyContent: "space-between",
    minHeight: "100%",
    width: "100%",
    gap: 32,
    padding: 16,
  },
  forgotPasswordContainer: {
    flexDirection: "column",
    alignItems: "flex-end",
    width: "100%",
    paddingTop: 12,
    paddingBottom: 32,
  },
  signupPrompt: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 24,
  },
  oauthButtons: {
    gap: 12,
    width: "100%",
  },
});

export default Signin;
